import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquarePlus, Search, Plus, FolderGit2 } from 'lucide-react';
import { ConversationItem } from '@/components/conversations/ConversationItem';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useProject } from '@/contexts/ProjectContext';
import { listConversations, listWorkflowRuns } from '@/lib/api';
import type { CodebaseResponse } from '@/lib/api';
import { AddProjectDialog } from '@/components/projects/AddProjectDialog';
import { cn } from '@/lib/utils';

interface ConversationListPanelProps {
  /** Called after the user picks "New Chat" or an existing conversation (e.g. close a drawer). */
  onNavigate?: () => void;
}

export function ConversationListPanel({
  onNavigate,
}: ConversationListPanelProps): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedProjectId, setSelectedProjectId, codebases, isLoadingCodebases } = useProject();
  const [searchQuery, setSearchQuery] = useState('');
  const [addProjectOpen, setAddProjectOpen] = useState(false);

  const { data: conversations } = useQuery({
    queryKey: ['conversations', selectedProjectId],
    queryFn: () => listConversations(selectedProjectId ?? undefined),
    refetchInterval: 10_000,
  });

  const { data: runs } = useQuery({
    queryKey: ['workflow-runs-status'],
    queryFn: () => listWorkflowRuns({ limit: 50 }),
    refetchInterval: 10_000,
  });

  const conversationStatusMap = useMemo((): Map<string, 'running' | 'failed'> => {
    const map = new Map<string, 'running' | 'failed'>();
    if (!runs) return map;
    for (const run of runs) {
      const key = run.parent_conversation_id ?? run.conversation_id;
      if (run.status === 'running') {
        map.set(key, 'running');
      } else if (run.status === 'failed' && !map.has(key)) {
        map.set(key, 'failed');
      }
    }
    return map;
  }, [runs]);

  const codebaseMap = useMemo((): Map<string, CodebaseResponse> => {
    const map = new Map<string, CodebaseResponse>();
    if (codebases) {
      for (const cb of codebases) map.set(cb.id, cb);
    }
    return map;
  }, [codebases]);

  const filtered = useMemo(
    () =>
      conversations?.filter(conv => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (conv.title ?? conv.platform_conversation_id).toLowerCase().includes(query);
      }),
    [conversations, searchQuery]
  );

  const handleNewChat = (): void => {
    navigate('/chat');
    onNavigate?.();
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="px-3 pt-3 pb-2">
        <button
          onClick={handleNewChat}
          className="flex w-full items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-accent-hover transition-colors"
        >
          <MessageSquarePlus className="h-4 w-4 shrink-0" />
          New Chat
        </button>
      </div>

      <div className="px-3 pb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
            Project
          </span>
          <button
            onClick={(): void => {
              setAddProjectOpen(true);
            }}
            className="p-1 rounded hover:bg-surface-elevated transition-colors"
            title="Add project"
          >
            <Plus className="h-3.5 w-3.5 text-text-tertiary hover:text-primary" />
          </button>
        </div>

        <AddProjectDialog
          open={addProjectOpen}
          onOpenChange={setAddProjectOpen}
          onSuccess={(codebase: CodebaseResponse): void => {
            void queryClient.invalidateQueries({ queryKey: ['codebases'] });
            setSelectedProjectId(codebase.id);
          }}
        />

        {isLoadingCodebases ? (
          <div className="flex items-center justify-center py-2">
            <span className="text-xs text-text-tertiary">Loading...</span>
          </div>
        ) : (
          <select
            value={selectedProjectId ?? ''}
            onChange={(e): void => {
              setSelectedProjectId(e.target.value || null);
            }}
            className="w-full rounded-md border border-border bg-surface-elevated px-2 py-1.5 text-xs text-text-primary focus:border-primary focus:outline-none"
          >
            <option value="">All Projects</option>
            {codebases?.map(cb => (
              <option key={cb.id} value={cb.id}>
                {cb.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <Separator className="bg-border" />

      <div className="px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-tertiary" />
          <input
            value={searchQuery}
            onChange={(e): void => {
              setSearchQuery(e.target.value);
            }}
            placeholder="Search..."
            className="w-full rounded-md border border-border bg-surface-elevated py-1.5 pl-7 pr-2 text-xs text-text-primary placeholder:text-text-tertiary focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0 px-2 pb-2">
        <div className="flex flex-col gap-0.5" onClick={onNavigate}>
          {filtered && filtered.length > 0 ? (
            filtered.map(conv => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                projectName={conv.codebase_id ? codebaseMap.get(conv.codebase_id)?.name : undefined}
                status={conversationStatusMap.get(conv.id) ?? 'idle'}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 py-8 px-4">
              <FolderGit2 className="h-8 w-8 text-text-tertiary" />
              <span className={cn('text-xs text-text-tertiary text-center')}>
                {conversations && conversations.length > 0
                  ? 'No matching conversations'
                  : 'No conversations yet — start a new chat!'}
              </span>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
